/** SPU-level product identifier in catalog context. */
export type ProductId = string;
export type VariantId = string;

/** Catalog product type key (`CatalogProductTypeMeta.code`), UPPER_SNAKE — extensible beyond seed defaults. */
export type ProductType = string;

/**
 * Hard cap on product `description` length, shared by API DTOs and admin UI
 * so the counter + server validation never drift. 2000 chars covers richest
 * PDP copy (Amazon-style) while staying well below DB text-row limits and
 * keeping payloads cheap.
 */
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 2000;

/**
 * Hard cap on product fiche-detail values (INCI / ingredients, usage, etc.).
 * Shared by API DTOs and admin UI. Larger than description: INCI lists routinely
 * exceed 2000 chars.
 */
export const PRODUCT_DETAIL_VALUE_MAX_LENGTH = 8000;

/** Value shape for a product fiche type. Stored as a string; validated by kind. */
export const PRODUCT_DETAIL_KINDS = ['TEXT', 'LIST', 'NUMBER'] as const;
export type ProductDetailKindCode = (typeof PRODUCT_DETAIL_KINDS)[number];

/** Seeded product detail type code for INCI / ingredients lists. */
export const PRODUCT_DETAIL_TYPE_INGREDIENTS = 'INGREDIENTS';

export type CanonicalProductDetailType = {
  code: string;
  name: string;
  kind: ProductDetailKindCode;
  sortOrder: number;
};

/** Catalog-wide fiche types upserted by `pnpm db:seed`. Staff may add more. */
export const CANONICAL_PRODUCT_DETAIL_TYPES: readonly CanonicalProductDetailType[] = [
  { code: PRODUCT_DETAIL_TYPE_INGREDIENTS, name: 'Ingrédients', kind: 'LIST', sortOrder: 10 },
  { code: 'USAGE', name: 'Mode d’emploi', kind: 'TEXT', sortOrder: 20 },
  { code: 'PRECAUTIONS', name: 'Précautions', kind: 'TEXT', sortOrder: 30 },
  { code: 'CONSERVATION', name: 'Conservation', kind: 'TEXT', sortOrder: 40 },
  { code: 'ACTIVES', name: 'Actifs', kind: 'LIST', sortOrder: 50 },
  { code: 'POSOLOGY', name: 'Posologie', kind: 'TEXT', sortOrder: 60 },
  { code: 'OLFACTIVE_FAMILY', name: 'Famille olfactive', kind: 'TEXT', sortOrder: 70 },
  { code: 'OLFACTIVE_NOTES', name: 'Notes olfactives', kind: 'LIST', sortOrder: 80 },
];

/** Sample values shown in setup / admin tooltips (French). */
export type ProductDetailTypeValueExamples = {
  blurb: string;
  examples: readonly string[];
};

export const PRODUCT_DETAIL_TYPE_VALUE_EXAMPLES_FR: Readonly<
  Record<string, ProductDetailTypeValueExamples>
> = {
  [PRODUCT_DETAIL_TYPE_INGREDIENTS]: {
    blurb: 'Liste INCI — un ingrédient par ligne.',
    examples: ['Aqua', 'Glycerin', 'Niacinamide', 'Tocopherol', 'Parfum'],
  },
  USAGE: {
    blurb: 'Texte libre — mode d’emploi.',
    examples: ['Appliquer matin et soir sur une peau propre et sèche.'],
  },
  PRECAUTIONS: {
    blurb: 'Texte libre — avertissements.',
    examples: ['Éviter le contact avec les yeux. Tenir hors de portée des enfants.'],
  },
  CONSERVATION: {
    blurb: 'Texte libre — conditions de stockage.',
    examples: ['Conserver à température ambiante, à l’abri de la lumière.'],
  },
  ACTIVES: {
    blurb: 'Liste — actifs mis en avant, un par ligne.',
    examples: ['Vitamine C 15 %', 'Acide hyaluronique', 'Rétinol'],
  },
  POSOLOGY: {
    blurb: 'Texte libre — posologie / dose.',
    examples: ['1 gélule par jour au petit-déjeuner, avec un verre d’eau.'],
  },
  OLFACTIVE_FAMILY: {
    blurb: 'Texte libre — famille olfactive dominante.',
    examples: ['Florale', 'Boisée', 'Orientale', 'Hespéridée'],
  },
  OLFACTIVE_NOTES: {
    blurb: 'Liste — notes (tête / cœur / fond), une par ligne.',
    examples: ['Bergamote', 'Jasmin', 'Bois de santal', 'Musc'],
  },
};

/** Fallback examples when the type code is custom (by value shape). */
export const PRODUCT_DETAIL_KIND_VALUE_EXAMPLES_FR: Readonly<
  Record<ProductDetailKindCode, ProductDetailTypeValueExamples>
> = {
  TEXT: {
    blurb: 'Texte libre saisi sur la fiche produit.',
    examples: ['Phrase ou paragraphe court décrivant l’attribut.'],
  },
  LIST: {
    blurb: 'Liste — un item par ligne.',
    examples: ['Item A', 'Item B', 'Item C'],
  },
  NUMBER: {
    blurb: 'Nombre décimal (point).',
    examples: ['12.5', '100', '0.5'],
  },
};

export function productDetailTypeValueExamplesFr(
  code: string,
  kind?: ProductDetailKindCode,
): ProductDetailTypeValueExamples {
  const byCode = PRODUCT_DETAIL_TYPE_VALUE_EXAMPLES_FR[code];
  if (byCode) return byCode;
  if (kind && PRODUCT_DETAIL_KIND_VALUE_EXAMPLES_FR[kind]) {
    return PRODUCT_DETAIL_KIND_VALUE_EXAMPLES_FR[kind];
  }
  return PRODUCT_DETAIL_KIND_VALUE_EXAMPLES_FR.TEXT;
}
export type CanonicalOptionValue = {
  code: string;
  label: string;
};

export type CanonicalOptionGroup = {
  code: string;
  name: string;
  values: readonly CanonicalOptionValue[];
};

/**
 * Variant option axes for Configuration initiale « Installer les modèles ».
 * Modest sample values — import may add more codes later.
 */
export const CANONICAL_OPTION_GROUPS: readonly CanonicalOptionGroup[] = [
  {
    code: 'CONTENANCE',
    name: 'Contenance',
    values: [
      { code: '30_ML', label: '30 ml' },
      { code: '50_ML', label: '50 ml' },
      { code: '100_ML', label: '100 ml' },
      { code: '200_ML', label: '200 ml' },
      { code: '500_ML', label: '500 ml' },
    ],
  },
  {
    code: 'FORMAT',
    name: 'Format',
    values: [
      { code: '30_CAPS', label: '30 capsules' },
      { code: '60_CAPS', label: '60 capsules' },
      { code: '90_CAPS', label: '90 capsules' },
    ],
  },
  {
    code: 'SPF',
    name: 'SPF',
    values: [
      { code: '15', label: 'SPF 15' },
      { code: '30', label: 'SPF 30' },
      { code: '50', label: 'SPF 50' },
    ],
  },
];

export type CanonicalProductTag = {
  /** Namespaced value stored on `Product.tags` (e.g. `routine:nettoyant`). */
  value: string;
  label: string;
};

export type CanonicalProductTagGroup = {
  /** Family code (`routine`, `skin`, …). */
  code: string;
  name: string;
  description: string;
  tags: readonly CanonicalProductTag[];
};

/**
 * Product tag families for Configuration initiale « Installer les modèles ».
 * Must stay aligned with shared regimen / quiz prefixes (`routine:`, `skin:`, …).
 */
export const CANONICAL_PRODUCT_TAG_GROUPS: readonly CanonicalProductTagGroup[] = [
  {
    code: 'routine',
    name: 'Étape routine',
    description:
      'Obligatoire pour apparaître dans Regimen Builder (`routine-match`). Un seul tag étape par produit.',
    tags: [
      { value: 'routine:nettoyant', label: 'Nettoyant' },
      { value: 'routine:brume', label: 'Brume / essence' },
      { value: 'routine:serum', label: 'Sérum ciblé' },
      { value: 'routine:contour-yeux', label: 'Contour yeux' },
      { value: 'routine:spf', label: 'SPF' },
      { value: 'routine:double-nettoyage', label: 'Double nettoyage' },
      { value: 'routine:tonique', label: 'Tonique' },
      { value: 'routine:actif-nuit', label: 'Actif nuit' },
      { value: 'routine:soin-traitant', label: 'Soin traitant' },
      { value: 'routine:soin-reparateur', label: 'Soin réparateur' },
      { value: 'routine:creme-barriere', label: 'Crème barrière' },
    ],
  },
  {
    code: 'skin',
    name: 'Type de peau',
    description: 'Réponses quiz regimen / analyse peau (`skin:*`).',
    tags: [
      { value: 'skin:seche', label: 'Peau sèche' },
      { value: 'skin:grasse', label: 'Peau grasse' },
      { value: 'skin:mixte', label: 'Peau mixte' },
      { value: 'skin:sensible', label: 'Peau sensible' },
      { value: 'skin:normale', label: 'Peau normale' },
    ],
  },
  {
    code: 'concern',
    name: 'Préoccupation',
    description: 'Aligné sur les choix `concern` du flow (`concern:*`).',
    tags: [
      { value: 'concern:hydratation', label: 'Hydratation' },
      { value: 'concern:antiage', label: 'Anti-âge' },
      { value: 'concern:imperfections', label: 'Imperfections' },
      { value: 'concern:teint', label: 'Éclat du teint' },
      { value: 'concern:protection', label: 'Protection' },
    ],
  },
  {
    code: 'texture',
    name: 'Texture',
    description: 'Aligné sur les choix `texture` du flow (`texture:*`).',
    tags: [
      { value: 'texture:legere', label: 'Texture légère' },
      { value: 'texture:confort', label: 'Texture confort' },
      { value: 'texture:sans-parfum', label: 'Sans parfum' },
    ],
  },
];
